import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useChangePassword } from '../../../../app/hooks/user/useChangePassword';

export function useChangePasswordFormController() {
  const [isCurrentPasswordVisible, setIsCurrentPasswordVisible] =
    useState<boolean>(false);
  const [isNewPasswordVisible, setIsNewPasswordVisible] =
    useState<boolean>(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState<boolean>(false);

  const { changePasswordAsync, changePasswordIsPending } = useChangePassword();

  const schema = z
    .object({
      currentPassword: z
        .string()
        .nonempty(t('settings.changePassword.errors.currentPassword.required')),
      newPassword: z
        .string()
        .nonempty(t('settings.changePassword.errors.newPassword.required'))
        .min(8, t('settings.changePassword.errors.newPassword.minLength')),
      confirmPassword: z
        .string()
        .nonempty(t('settings.changePassword.errors.confirmPassword.required')),
    })
    .refine((data) => data.confirmPassword === data.newPassword, {
      message: t('settings.changePassword.errors.confirmPassword.mismatch'),
      path: ['confirmPassword'],
    });

  type FormData = z.infer<typeof schema>;

  const {
    handleSubmit: hookFormHandleSubmit,
    register,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const handleSubmit = hookFormHandleSubmit(async (data) => {
    try {
      await changePasswordAsync({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
    } catch {
      return;
    }
  });

  function handleToggleCurrentPasswordVisibility() {
    setIsCurrentPasswordVisible((prev) => !prev);
  }

  function handleToggleNewPasswordVisibility() {
    setIsNewPasswordVisible((prev) => !prev);
  }

  function handleToggleConfirmPasswordVisibility() {
    setIsConfirmPasswordVisible((prev) => !prev);
  }

  return {
    errors,
    isFetching: changePasswordIsPending,
    isCurrentPasswordVisible,
    isNewPasswordVisible,
    isConfirmPasswordVisible,
    register,
    handleSubmit,
    handleToggleCurrentPasswordVisibility,
    handleToggleNewPasswordVisibility,
    handleToggleConfirmPasswordVisibility,
  };
}
